import React, { useEffect, useState } from 'react';
import axios from 'axios';

const App = () => {
  const [todayCheckIn, setTodayCheckIn] = useState(0);
  const [todayCheckOut, setTodayCheckOut] = useState(0);
  const [totalInHotel, setTotalInHotel] = useState(0);
  const [totalAvailableRoom, setTotalAvailableRoom] = useState(0);
  const [totalOccupiedRoom, setTotalOccupiedRoom] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to check if a date is today (compares only date part)
  const isToday = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Helper function to check if a booking is currently "in hotel"
  const isInHotel = (checkInDateString, checkOutDateString, status) => {
    // Cancelled bookings are never considered "in hotel"
    if (status === 'Cancelled') return false;

    const checkIn = new Date(checkInDateString);
    const checkOut = new Date(checkOutDateString);
    const today = new Date();

    // Set hours to 00:00:00 for accurate date-only comparison for "in hotel" status
    checkIn.setHours(0, 0, 0, 0);
    checkOut.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    // A booking is "in hotel" if today's date is on or after check-in date
    // AND today's date is on or before check-out date.
    return today >= checkIn && today <= checkOut;
  };


  useEffect(() => {
    const fetchOverviewData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch hotel bookings data from the 'hotel_bookings' table
        const bookingsResponse = await axios.get('https://admin.thepridehotel.in/api/hotel_bookings');
        const hotelBookings = bookingsResponse.data;

        // Fetch room inventory data from the 'inventory' table
        const inventoryResponse = await axios.get('https://admin.thepridehotel.in/api/inventory');
        const inventory = inventoryResponse.data;

        // Initialize counters for various metrics
        let checkInsCount = 0;
        let checkOutsCount = 0;
        let activeBookingsCount = 0; // Represents "Total In hotel"
        let futureReservedBookingsCount = 0; // For "Total Occupied Room"

        // Get today's date at midnight for comparison
        const todayAtMidnight = new Date();
        todayAtMidnight.setHours(0, 0, 0, 0);

        hotelBookings.forEach(booking => {
          // Calculate Today's Check-in
          if (isToday(booking.check_in)) {
            checkInsCount++;
          }
          // Calculate Today's Check-out
          if (isToday(booking.check_out)) {
            checkOutsCount++;
          }
          // Calculate Total In hotel (active bookings)
          if (isInHotel(booking.check_in, booking.check_out, booking.status)) {
            activeBookingsCount++;
          }

          // Calculate "reserved rooms" that are not yet "in hotel"
          // These are bookings with 'Reserved' status where their check-in date is in the future.
          const bookingCheckInDate = new Date(booking.check_in);
          bookingCheckInDate.setHours(0, 0, 0, 0);

          if (booking.status === 'Reserved' && bookingCheckInDate > todayAtMidnight) {
            futureReservedBookingsCount++;
          }
        });

        setTodayCheckIn(checkInsCount);
        setTodayCheckOut(checkOutsCount);
        setTotalInHotel(activeBookingsCount);

        // Calculate Total Occupied Room based on "reserved room + check in to check out"
        // This is interpreted as: active bookings (currently in hotel) + future reserved bookings
        const calculatedTotalOccupiedRoom = activeBookingsCount + futureReservedBookingsCount;
        setTotalOccupiedRoom(calculatedTotalOccupiedRoom);

        // Calculate Total Hotel Capacity from inventory by summing the 'available' column
        // (Assuming 'available' in inventory represents the total count of rooms for that type)
        let totalHotelCapacity = 0;
        inventory.forEach(room => {
          totalHotelCapacity += room.available;
        });

        // Calculate Total Available Room as: Total Hotel Capacity - Total Occupied Room
        setTotalAvailableRoom(totalHotelCapacity - calculatedTotalOccupiedRoom);

      } catch (err) {
        console.error('Error fetching overview data:', err);
        setError('Failed to load overview data. Please ensure your backend server is running and API endpoints are correct for hotel_bookings and inventory tables.');
      } finally {
        setLoading(false);
      }
    };

    fetchOverviewData();

    // Optional: Refresh data every few minutes (e.g., every 5 minutes)
    const intervalId = setInterval(fetchOverviewData, 300000); // 300000 ms = 5 minutes

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, []); // Empty dependency array means this effect runs once on mount and cleans up on unmount


  if (loading) {
    return (
      <div className="overview-container">
        <h3 className="overview-title">Overview</h3>
        <div>Loading overview data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overview-container">
        <h3 className="overview-title">Overview</h3>
        <div style={{ color: 'red' }}>{error}</div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap');

        .overview-container {
          font-family: 'Open Sans', sans-serif;
          background: white;
          border-radius: 10px;
          padding: 20px;
          margin: 10px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          width: 98%;
          max-width: 100%;
          overflow-x: auto;
          box-sizing: border-box;
        }

        .overview-title {
          font-size: 20px;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 24px;
        }

        .overview-items {
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 20px;
        }

        .overview-item {
          text-align: left;
          flex: 1 1 calc(20% - 16px);
          min-width: 120px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .overview-label {
          color: #9ca3af;
          font-size: 14px;
          margin-bottom: 0;
          white-space: nowrap;
        }

        .overview-heading-row {
          display: flex;
          align-items: baseline;
          gap: 6px;
          white-space: nowrap;
        }

        .overview-heading {
          color: #4b5563;
          font-size: 16px;
        }

        .overview-number {
          color: #ae7d54;
          font-size: 24px;
          font-weight: bold;
        }

        @media (max-width: 768px) {
          .overview-items {
            gap: 15px;
            justify-content: flex-start;
          }
          .overview-item {
            flex: 1 1 calc(33.33% - 10px);
          }
        }

        @media (max-width: 600px) {
          .overview-items {
            flex-direction: column;
            align-items: center;
            padding-right: 10px; /* Added padding for better mobile view */
          }
          .overview-item {
            text-align: center;
            align-items: center;
            width: 100%;
            padding-right: 0;
            margin-bottom: 15px;
          }
          .overview-item:last-child {
            margin-bottom: 0;
          }
        }
      `}</style>

      <div className="overview-container">
        <h3 className="overview-title">Overview</h3>
        <div className="overview-items">
          <div className="overview-item">
            <div className="overview-label">Today's</div>
            <div className="overview-heading-row">
              <div className="overview-heading">Check-in</div>
              <div className="overview-number">
                {todayCheckIn.toString().padStart(2, '0')}
              </div>
            </div>
          </div>
          <div className="overview-item">
            <div className="overview-label">Today's</div>
            <div className="overview-heading-row">
              <div className="overview-heading">Check-out</div>
              <div className="overview-number">
                {todayCheckOut.toString().padStart(2, '0')}
              </div>
            </div>
          </div>
          <div className="overview-item">
            <div className="overview-label">Total</div>
            <div className="overview-heading-row">
              <div className="overview-heading">In hotel</div>
              <div className="overview-number">
                {totalInHotel.toString().padStart(2, '0')}
              </div>
            </div>
          </div>
          <div className="overview-item">
            <div className="overview-label">Total</div>
            <div className="overview-heading-row">
              <div className="overview-heading">Available room</div>
              <div className="overview-number">
                {totalAvailableRoom.toString().padStart(2, '0')}
              </div>
            </div>
          </div>
          <div className="overview-item">
            <div className="overview-label">Total</div>
            <div className="overview-heading-row">
              <div className="overview-heading">Occupied room</div>
              <div className="overview-number">
                {totalOccupiedRoom.toString().padStart(2, '0')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default App;